"use client";

import { CustomerStoryRotatorBlock } from "@mw/shared/payload-types";
import { BaseLink, Link } from "@ui/components/core/Link";
import { pxToRem } from "@ui/lib/utils";
import { formatLink, getImageProps, getLinkProps } from "@utilities/payload";
import clsx from "clsx";
import { AnimatePresence, useInView } from "motion/react";
import * as m from "motion/react-m";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Tabs, TabList, Tab, TabPanel, Key } from "react-aria-components";

function roundRectPath(width: number, height: number, r = 0, x = 0, y = 0) {
  const v0 = { x: x, y: y };
  const v1 = { x: x, y: y + height };
  const v2 = { x: x + width, y: y + height };
  const v3 = { x: x + width, y: y };

  return `M${v0.x} ${v0.y + height / 2} V${v1.y - r} C${v1.x} ${v1.y} ${
    v1.x + r
  } ${v1.y} ${v1.x + r} ${v1.y} H${v2.x - r} \
C${v2.x} ${v2.y} ${v2.x} ${v2.y - r} ${v2.x} ${v2.y - r} V${v3.y + r} C${
    v3.x
  } ${v3.y} ${v3.x - r} ${v3.y} ${v3.x - r} ${v3.y} \
H${v0.x + r} C${v0.x} ${v0.y} ${v0.x} ${v0.y + r} ${v0.x} ${v0.y + r} Z`;
}

const DURATION = 0.3;
const ROTATE_DELAY = 5;

function CustomerLogo({
  id,
  customer,
  isActive,
  isPaused,
  onComplete,
}: {
  id: string;
  customer: NonNullable<CustomerStoryRotatorBlock["customers"]>[number];
  isActive: boolean;
  isPaused: boolean;
  onComplete: () => void;
}) {
  const animate = !isPaused;
  const logoProps = getImageProps(customer.logo);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (isActive) {
      hasCompletedRef.current = false;
    }
  }, [isActive]);

  return (
    <Tab id={id} className='tw-group tw-cursor-pointer tw-outline-none tw-relative' aria-label={customer.name}>
      <div className='tw-absolute -tw-inset-1.5 tw-rounded-2xl tw-pointer-events-none group-focus-visible:tw-outline-focus' />
      <svg
        viewBox='0 0 54 54'
        aria-hidden
        style={{
          width: pxToRem(54),
          height: pxToRem(54),
          margin: pxToRem(-5),
        }}
      >
        <AnimatePresence>
          {isActive && (
            <m.path
              d={roundRectPath(52, 52, 16, 1, 1)}
              fill='none'
              strokeWidth='1.5'
              stroke={isActive ? customer.color : "#E0DFDD"}
              strokeLinecap='round'
              animate={
                animate
                  ? {
                      pathLength: 1,
                      visibility: "visible",
                      transition: {
                        pathLength: { duration: ROTATE_DELAY, delay: DURATION, ease: "linear" },
                        visibility: { delay: DURATION },
                      },
                    }
                  : undefined
              }
              style={{ transform: "rotate(90deg) scale(1, -1)" }}
              initial={{ opacity: 1, pathLength: 0, visibility: "hidden" }}
              exit={{ opacity: 0, transition: { duration: DURATION } }}
              onUpdate={
                animate
                  ? latest => {
                      if (latest.pathLength === 1 && !hasCompletedRef.current) {
                        hasCompletedRef.current = true;
                        onComplete();
                      }
                    }
                  : undefined
              }
            />
          )}
        </AnimatePresence>
        <m.rect
          width='44'
          height='44'
          x='5'
          y='5'
          rx='12'
          initial={false}
          animate={
            isActive
              ? { fill: customer.color, transition: { duration: DURATION, delay: DURATION } }
              : { fill: "#E0DFDD", transition: { duration: DURATION } }
          }
        />
      </svg>
      {logoProps && (
        <Image {...logoProps} alt='' className='tw-size-full tw-absolute tw-inset-0 tw-brightness-0 tw-invert' />
      )}
    </Tab>
  );
}

export function BlockCustomerStoryRotator({ title, customers, primaryLink, secondaryLink }: CustomerStoryRotatorBlock) {
  const [selected, setSelected] = useState<Key>(customers?.[0]?.name ?? "");
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const [isFirstRender, setIsFirstRender] = useState(true);

  if (!customers || customers.length === 0) {
    return null;
  }

  const isPaused = !inView || customers.length === 1;

  const primary = formatLink(primaryLink);
  const secondary = formatLink(secondaryLink);
  const hasPrimary = primary && primary.label?.trim();
  const hasSecondary = secondary && secondary.label?.trim();
  const buttonCount = hasPrimary && hasSecondary ? 2 : hasPrimary || hasSecondary ? 1 : 0;

  return (
    <div
      className={clsx(
        "tw-relative tw-flex tw-flex-col tw-gap-x-10 tw-gap-y-6",
        buttonCount === 2 ? "md:tw-flex-row md:tw-justify-between" : "sm:tw-flex-row sm:tw-justify-between",
      )}
    >
      <Tabs
        ref={ref}
        selectedKey={selected}
        onSelectionChange={value => {
          setSelected(value);
          setIsFirstRender(false);
        }}
        className={clsx(
          "sm:tw-flex-auto tw-flex tw-flex-col tw-gap-x-5 tw-gap-y-3",
          buttonCount === 2
            ? "lg:tw-flex-row lg:tw-items-start"
            : buttonCount === 1
              ? "md:tw-flex-row md:tw-items-start"
              : "sm:tw-flex-row sm:tw-items-start",
        )}
        aria-label={title}
      >
        <TabList className='tw-flex tw-flex-none tw-gap-4'>
          {customers.map(customer => (
            <CustomerLogo
              key={customer.name}
              id={customer.name}
              customer={customer}
              isPaused={isPaused}
              isActive={customer.name === selected}
              onComplete={() => {
                const currentIndex = customers.findIndex(c => c.name === selected);
                const nextIndex = currentIndex === customers.length - 1 ? 0 : currentIndex + 1;
                setSelected(customers[nextIndex].name);
                setIsFirstRender(false);
              }}
            />
          ))}
        </TabList>
        <div className='tw-grid tw-flex-auto'>
          {customers.map(customer => {
            const link = getLinkProps(customer.link);
            const titleClassName = clsx(
              "tw-font-medium tw-text-black tw-type-sm tw-block",
              link &&
                "tw-relative tw-pr-4 tw-outline-none tw-rounded-sm hover:tw-underline hover:tw-decoration-1.5 hover:tw-underline-offset-2",
            );

            return (
              <TabPanel
                key={customer.name}
                id={customer.name}
                shouldForceMount
                className='tw-w-fit tw-col-start-1 tw-row-start-1 tw-outline-none focus-visible:tw-outline-focus has-[:focus-visible]:tw-outline-focus tw-rounded'
              >
                {({ isInert }) => (
                  <m.div
                    key={isInert ? customer.name : `${customer.name}-${selected}`}
                    className='tw-py-1'
                    initial={
                      isFirstRender ? false : !isInert ? { maskPosition: "100% 0%" } : { maskPosition: "100% 0%" }
                    }
                    style={{
                      maskImage: isInert
                        ? "linear-gradient(to left, white, white, transparent, transparent)"
                        : "linear-gradient(to right, white, white, transparent, transparent)",
                      maskSize: "300% 100%",
                      maskRepeat: "no-repeat",
                    }}
                    animate={
                      isInert
                        ? {
                            maskPosition: "0% 0%",
                            transition: { duration: 0.6 },
                          }
                        : {
                            maskPosition: "0% 0%",
                            transition: { duration: 0.6, delay: 0.3 },
                          }
                    }
                  >
                    <p className='tw-w-fit'>
                      {link ? (
                        <BaseLink href={link.href} className={titleClassName}>
                          {customer.name}
                          <svg
                            viewBox='0 0 24 24'
                            fill='none'
                            aria-hidden
                            className='tw-size-3 tw-absolute tw-right-0 -tw-bottom-0.25'
                          >
                            <path
                              d='M18.25 15.25V5.75M18.25 5.75H8.75M18.25 5.75L6 18'
                              stroke='currentColor'
                              strokeWidth='3'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </BaseLink>
                      ) : (
                        <span className={titleClassName}>{customer.name}</span>
                      )}
                    </p>
                    <p className='tw-mt-3 tw-type-sm tw-text-gray-500 tw-text-pretty'>{customer.description}</p>
                  </m.div>
                )}
              </TabPanel>
            );
          })}
        </div>
      </Tabs>
      {(hasPrimary || hasSecondary) && (
        <div
          className={clsx(
            "tw-flex-none tw-flex tw-flex-wrap tw-gap-2 tw-flex-row-reverse tw-justify-end",
            buttonCount === 2
              ? "md:tw-flex-row md:tw-justify-start md:tw-absolute md:-tw-top-0.5 md:tw-right-0 lg:tw-static lg:-tw-my-0.5"
              : "sm:tw-flex-row sm:tw-justify-start sm:tw-absolute sm:-tw-top-0.5 sm:tw-right-0 md:tw-static md:-tw-my-0.5",
          )}
        >
          {hasSecondary && <Link {...secondary} asButton buttonVariants={{ size: "lg", variant: "elevated" }} />}
          {hasPrimary && <Link {...primary} asButton buttonVariants={{ size: "lg" }} />}
        </div>
      )}
    </div>
  );
}
